/**
 * CreateMeetingModal — Dialog for scheduling a new meeting
 */

import { useState, useEffect } from 'react';
import { fromZonedTime } from 'date-fns-tz';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useCreateMeeting } from '../hooks/useMeetings';
import { useAgendas } from '../hooks/useAgendas';

// =============================================================================
// Helpers
// =============================================================================

function toISOScheduledAt(date: string, time: string): string {
  // Combine date (YYYY-MM-DD) and time (HH:MM) into an ISO string.
  // Treat input as America/Montreal wall-clock time so the stored UTC value
  // is correct regardless of the user's browser timezone.
  if (!date || !time) return '';
  return fromZonedTime(`${date}T${time}:00`, 'America/Montreal').toISOString();
}

function todayDate(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Montreal' });
}

// =============================================================================
// CreateMeetingModal
// =============================================================================

interface CreateMeetingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId?: string;
}

export function CreateMeetingModal({ open, onOpenChange, teamId }: CreateMeetingModalProps) {
  const [agendaId, setAgendaId] = useState('');
  const [agendaTouched, setAgendaTouched] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [ownerId, setOwnerId] = useState('');
  const [scribeId, setScribeId] = useState('');

  const createMeeting = useCreateMeeting();
  const { data: agendasData, isLoading: agendasLoading } = useAgendas({ team_id: teamId });

  const agendas = agendasData?.agendas ?? [];

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAgendaId('');
      setAgendaTouched(false);
      setDate(todayDate());
      setTime('09:00');
      setOwnerId('');
      setScribeId('');
    }
  }, [open]);

  // Auto-select first agenda when list loads
  useEffect(() => {
    if (agendas.length > 0 && !agendaId) {
      setAgendaId(agendas[0].id);
    }
  }, [agendas, agendaId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!teamId) {
      toast.error('No team selected. Please select a team first.');
      return;
    }

    if (!agendaId) {
      setAgendaTouched(true);
      toast.error('Please select an agenda.');
      return;
    }

    const scheduledAt = toISOScheduledAt(date, time);
    if (!scheduledAt) {
      toast.error('Please enter a valid date and time.');
      return;
    }

    try {
      await createMeeting.mutateAsync({
        agenda_id: agendaId,
        team_id: teamId,
        scheduled_at: scheduledAt,
        ...(ownerId.trim() ? { owner_id: ownerId.trim() } : {}),
        ...(scribeId.trim() ? { scribe_id: scribeId.trim() } : {}),
      });
      toast.success('Meeting scheduled');
      onOpenChange(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error('Failed to schedule meeting', { description: message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schedule a Meeting</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-agenda">
                Agenda <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Select
                value={agendaId}
                onValueChange={(v) => { setAgendaId(v); setAgendaTouched(true); }}
                disabled={agendasLoading || agendas.length === 0}
              >
                <SelectTrigger
                  id="meeting-agenda"
                  aria-required="true"
                  aria-invalid={agendaTouched && !agendaId}
                  className={agendaTouched && !agendaId ? 'border-destructive' : ''}
                >
                  <SelectValue
                    placeholder={
                      agendasLoading
                        ? 'Loading agendas...'
                        : agendas.length === 0
                          ? 'No agendas available'
                          : 'Select an agenda'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {agendas.map((agenda) => (
                    <SelectItem key={agenda.id} value={agenda.id}>
                      {agenda.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {agendaTouched && !agendaId && (
                <p className="text-xs text-destructive" role="alert">
                  An agenda is required to schedule a meeting.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="meeting-date">Date</Label>
                <Input
                  id="meeting-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="meeting-time">Time</Label>
                <Input
                  id="meeting-time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meeting-owner">
                Owner{' '}
                <span className="text-muted-foreground font-normal">(user ID, optional)</span>
              </Label>
              <Input
                id="meeting-owner"
                placeholder="User ID"
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="meeting-scribe">
                Scribe{' '}
                <span className="text-muted-foreground font-normal">(user ID, optional)</span>
              </Label>
              <Input
                id="meeting-scribe"
                placeholder="User ID"
                value={scribeId}
                onChange={(e) => setScribeId(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMeeting.isPending || !agendaId}>
              {createMeeting.isPending ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
